import { zodResolver } from '@hookform/resolvers/zod';
import { heroSchema, HERO_PADRAO, type Ancora, type Hero } from '@plane/shared';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Field } from '@/components/field';
import { CampoDeImagem } from '@/modules/site-content/components/campo-de-imagem';
import { useHero, useSaveHero } from '@/modules/site-content/hooks/use-conteudo';
import { extrairYoutubeId } from '@/modules/site-content/youtube';
import { ApiRequestError } from '@/services/http-client';

/** Seções para onde os botões do banner podem apontar, com nome legível. */
const ANCORAS: readonly { readonly valor: Ancora; readonly rotulo: string }[] = [
  { valor: '#atelie', rotulo: 'Ateliê' },
  { valor: '#ambientes', rotulo: 'Ambientes' },
  { valor: '#projetos', rotulo: 'Projetos' },
  { valor: '#processo', rotulo: 'Processo' },
  { valor: '#showroom', rotulo: 'Showroom' },
  { valor: '#orcamento', rotulo: 'Orçamento' },
];

/**
 * Formato do formulário.
 *
 * Duas diferenças em relação ao contrato, as duas para a tela fazer sentido:
 * o vídeo é o texto que a pessoa cola — URL ou identificador —, e os números
 * viram objetos porque é o que o `useFieldArray` manipula bem.
 */
const formularioSchema = heroSchema
  .omit({ fundo: true, numeros: true })
  .extend({
    tipoDeFundo: z.enum(['solido', 'imagem', 'video']),
    video: z.string().trim(),
    mediaId: z.string().nullable(),
    numeros: z.array(z.object({ valor: z.string().trim().min(1).max(40) })).max(4),
  })
  // Os três campos convivem para que trocar de fundo não apague o que já foi
  // escolhido; a validação só cobra o campo do tipo que está ativo.
  .refine(
    (v) => v.tipoDeFundo !== 'video' || (v.video !== '' && extrairYoutubeId(v.video) !== null),
    { message: 'Cole o link do vídeo no YouTube.', path: ['video'] },
  )
  .refine((v) => v.tipoDeFundo !== 'imagem' || v.mediaId !== null, {
    message: 'Escolha uma imagem.',
    path: ['mediaId'],
  });

type Formulario = z.infer<typeof formularioSchema>;

function paraFormulario(hero: Hero): Formulario {
  const { fundo, numeros, ...resto } = hero;

  return {
    ...resto,
    tipoDeFundo: fundo.tipo,
    video: fundo.tipo === 'video' ? fundo.youtubeId : '',
    mediaId: fundo.tipo === 'imagem' ? fundo.mediaId : null,
    numeros: numeros.map((valor) => ({ valor })),
  };
}

function paraHero(valores: Formulario): Hero {
  const { tipoDeFundo, video, mediaId, numeros, ...resto } = valores;

  return {
    ...resto,
    numeros: numeros.map((n) => n.valor),
    fundo: montarFundo(tipoDeFundo, video, mediaId),
  };
}

function montarFundo(
  tipo: Formulario['tipoDeFundo'],
  video: string,
  mediaId: string | null,
): Hero['fundo'] {
  if (tipo === 'video') {
    const youtubeId = extrairYoutubeId(video);

    // O `refine` já barra este caso; o `if` existe porque o tipo permite `null`
    // e cair no sólido é melhor do que gravar um identificador vazio.
    return youtubeId === null ? { tipo: 'solido' } : { tipo: 'video', youtubeId };
  }

  if (tipo === 'imagem') {
    return mediaId === null ? { tipo: 'solido' } : { tipo: 'imagem', mediaId };
  }

  return { tipo: 'solido' };
}

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function HeroPage() {
  const consulta = useHero();
  const salvar = useSaveHero();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? HERO_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(hero) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(hero)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Hero;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (hero: Hero) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Formulario>({
    resolver: zodResolver(formularioSchema),
    defaultValues: paraFormulario(inicial),
  });

  const numeros = useFieldArray({ control, name: 'numeros' });
  const tipoDeFundo = watch('tipoDeFundo');
  const video = watch('video');
  const mediaId = watch('mediaId');
  const idDoVideo = video === '' ? null : extrairYoutubeId(video);

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit((valores) => aoEnviar(paraHero(valores)))(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Banner do topo</h1>
        <p className="mt-3 text-sm text-ink-soft">A primeira coisa que quem entra no site vê.</p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os campos abaixo estão com o conteúdo do protótipo — os
          três números embaixo do banner <strong>são dado de demonstração</strong> e precisam da sua
          conferência.
        </p>
      )}

      <Field
        id="kicker"
        label="Etiqueta acima do título"
        placeholder="Cascavel · PR — desde 2009"
        error={errors.kicker?.message}
        {...register('kicker')}
      />

      <AreaDeTexto
        id="titulo"
        rotulo="Título"
        linhas={3}
        erro={errors.titulo?.message}
        ajuda={
          <>
            Quebre a linha onde o título deve quebrar. <code>*assim*</code> deixa a palavra em
            dourado itálico; <code>_assim_</code> deixa vazada, só com o contorno.
          </>
        }
        {...register('titulo')}
      />

      <AreaDeTexto
        id="descricao"
        rotulo="Parágrafo"
        linhas={3}
        erro={errors.descricao?.message}
        {...register('descricao')}
      />

      <Botao
        titulo="Botão principal"
        idRotulo="chamada-principal-rotulo"
        idAncora="chamada-principal-ancora"
        erroRotulo={errors.chamadaPrincipal?.rotulo?.message}
        registroRotulo={register('chamadaPrincipal.rotulo')}
        registroAncora={register('chamadaPrincipal.ancora')}
      />

      <Botao
        titulo="Link secundário"
        idRotulo="chamada-secundaria-rotulo"
        idAncora="chamada-secundaria-ancora"
        erroRotulo={errors.chamadaSecundaria?.rotulo?.message}
        registroRotulo={register('chamadaSecundaria.rotulo')}
        registroAncora={register('chamadaSecundaria.ancora')}
      />

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Faixa de números
        </legend>

        <p className="mt-1.5 text-xs text-ink-soft">
          A tira embaixo do banner. Até quatro; deixe vazia para escondê-la.
        </p>

        <ul className="mt-3 flex list-none flex-col gap-3 p-0">
          {numeros.fields.map((campo, indice) => (
            <li key={campo.id} className="flex items-end gap-3">
              <div className="flex-1">
                <Field
                  id={`numero-${String(indice)}`}
                  label={`Item ${String(indice + 1)}`}
                  placeholder="16 anos"
                  error={errors.numeros?.[indice]?.valor?.message}
                  {...register(`numeros.${indice}.valor`)}
                />
              </div>
              <button
                type="button"
                onClick={() => numeros.remove(indice)}
                className="pb-3 text-xs text-brass underline"
              >
                Remover
              </button>
            </li>
          ))}
        </ul>

        {numeros.fields.length < 4 && (
          <button
            type="button"
            onClick={() => numeros.append({ valor: '' })}
            className="mt-3 text-sm text-brand-700 underline"
          >
            Adicionar item
          </button>
        )}
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Fundo do banner
        </legend>

        <div className="mt-3 flex flex-col gap-2">
          {(
            [
              ['solido', 'Verde da marca', 'Sem imagem nem vídeo.'],
              ['imagem', 'Uma foto', 'Sobe do seu computador, cobrindo a tela toda.'],
              ['video', 'Um vídeo do YouTube', 'Sem som, em laço e sem controles.'],
            ] as const
          ).map(([valor, rotulo, nota]) => (
            <label key={valor} className="flex items-start gap-2.5 text-sm">
              <input type="radio" value={valor} className="mt-1" {...register('tipoDeFundo')} />
              <span>
                {rotulo}
                <span className="block text-xs text-ink-soft">{nota}</span>
              </span>
            </label>
          ))}
        </div>

        {tipoDeFundo === 'imagem' && (
          <div className="mt-4">
            <CampoDeImagem
              mediaId={mediaId}
              aoTrocar={(id) => setValue('mediaId', id, { shouldValidate: true })}
            />
            {errors.mediaId && (
              <p role="alert" className="mt-2 text-xs text-brass">
                {errors.mediaId.message}
              </p>
            )}
          </div>
        )}

        {tipoDeFundo === 'video' && (
          <div className="mt-4">
            <Field
              id="video"
              label="Link do vídeo no YouTube"
              placeholder="https://www.youtube.com/watch?v=..."
              error={errors.video?.message}
              {...register('video')}
            />

            <p className="mt-2 text-xs text-ink-soft">
              <strong>Duas ressalvas honestas:</strong> celular costuma recusar vídeo que toca
              sozinho — nesses aparelhos aparece o fundo verde, não um retângulo preto. E quem tiver
              &ldquo;reduzir movimento&rdquo; ligado no sistema também vê o fundo verde, de
              propósito.
            </p>

            {idDoVideo !== null && (
              <div className="mt-4">
                <p className="text-xs tracking-[0.12em] text-ink-soft uppercase">Prévia</p>
                <img
                  src={`https://i.ytimg.com/vi/${idDoVideo}/mqdefault.jpg`}
                  alt=""
                  width={320}
                  height={180}
                  className="mt-2 w-full max-w-[320px] border border-clay"
                />
              </div>
            )}
          </div>
        )}
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}

function Botao({
  titulo,
  idRotulo,
  idAncora,
  erroRotulo,
  registroRotulo,
  registroAncora,
}: {
  titulo: string;
  idRotulo: string;
  idAncora: string;
  erroRotulo?: string | undefined;
  registroRotulo: ReturnType<ReturnType<typeof useForm<Formulario>>['register']>;
  registroAncora: ReturnType<ReturnType<typeof useForm<Formulario>>['register']>;
}) {
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">{titulo}</legend>

      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <Field id={idRotulo} label="Texto" error={erroRotulo} {...registroRotulo} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor={idAncora} className="text-xs tracking-[0.12em] text-ink-soft uppercase">
            Leva para
          </label>
          <select
            id={idAncora}
            className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none"
            {...registroAncora}
          >
            {ANCORAS.map((ancora) => (
              <option key={ancora.valor} value={ancora.valor}>
                {ancora.rotulo}
              </option>
            ))}
          </select>
        </div>
      </div>
    </fieldset>
  );
}

function AreaDeTexto({
  id,
  rotulo,
  linhas,
  erro,
  ajuda,
  ref,
  ...resto
}: {
  id: string;
  rotulo: string;
  linhas: number;
  erro?: string | undefined;
  ajuda?: React.ReactNode;
} & React.ComponentPropsWithRef<'textarea'>) {
  const idAjuda = ajuda ? `${id}-ajuda` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs tracking-[0.12em] text-ink-soft uppercase">
        {rotulo}
      </label>

      <textarea
        {...resto}
        ref={ref}
        id={id}
        rows={linhas}
        aria-invalid={erro ? true : undefined}
        aria-describedby={idAjuda}
        className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
      />

      {ajuda && (
        <p id={idAjuda} className="text-xs text-ink-soft">
          {ajuda}
        </p>
      )}

      {erro && (
        <p role="alert" className="text-xs text-brass">
          {erro}
        </p>
      )}
    </div>
  );
}
